import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StraightenIcon from '@mui/icons-material/Straighten';
import EngineeringIcon from '@mui/icons-material/Engineering';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WarningIcon from '@mui/icons-material/Warning';
import RecommendIcon from '@mui/icons-material/ThumbUp';
import { getFeasibilityStudy, saveFeasibilityStudy, fetchFeasibilityStudyFromSOW } from '../../utils/discoveryPhaseService';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const FeasibilityStudyPage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();
  
  const [study, setStudy] = useState({
    executiveSummary: '',
    technicalFeasibility: [],
    businessFeasibility: [],
    financialFeasibility: [],
    riskAssessment: [],
    recommendations: []
  });
  
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editingStudy, setEditingStudy] = useState({
    executiveSummary: '',
    technicalFeasibility: [],
    businessFeasibility: [],
    financialFeasibility: [],
    riskAssessment: [],
    recommendations: []
  });
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogItem, setDialogItem] = useState({});
  const [dialogItemIndex, setDialogItemIndex] = useState(null);
  
  // Load feasibility study data from database when component mounts
  useEffect(() => {
    if (projectId) {
      loadFeasibilityStudyFromDB();
    }
  }, [projectId]);
  
  // Load feasibility study from database
  const loadFeasibilityStudyFromDB = async () => {
    setLoading(true);
    try {
      const studyData = await getFeasibilityStudy(projectId);
      if (studyData) {
        setStudy(studyData);
        setEditingStudy(studyData);
      }
    } catch (error) {
      console.error('Error loading feasibility study:', error);
      showSnackbar('Error loading feasibility study data', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Save feasibility study to database
  const handleSave = async () => {
    setLoading(true);
    try {
      await saveFeasibilityStudy(projectId, editingStudy);
      setStudy(editingStudy);
      setEditMode(false);
      showSnackbar('Feasibility study saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving feasibility study:', error);
      showSnackbar('Error saving feasibility study', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch feasibility study from project documents using AI
  const handleFetchFromSOW = async () => {
    setAiLoading(true);
    try {
      const response = await fetchFeasibilityStudyFromSOW(projectId);
      if (response.data) {
        setEditingStudy(response.data);
        setStudy(response.data);
        showSnackbar('Feasibility study generated from project documents successfully!', 'success');
      }
    } catch (error) {
      console.error('Error fetching feasibility study from project documents:', error);
      showSnackbar('Error generating feasibility study from project documents', 'error');
    } finally {
      setAiLoading(false);
    }
  };
  
  // Dialog functions
  const handleOpenDialog = (type, item = null, index = null) => {
    setDialogType(type);
    
    if (type === 'technicalFeasibility') {
      setDialogItem(item || { 
        aspect: '', 
        description: '', 
        assessment: '', 
        feasibility: 'Feasible', 
        challenges: [], 
        requirements: [], 
        timeline: '' 
      });
    } else if (type === 'businessFeasibility') {
      setDialogItem(item || { 
        aspect: '', 
        description: '', 
        marketAnalysis: '', 
        feasibility: 'Feasible', 
        benefits: [], 
        challenges: [], 
        success: '' 
      });
    } else if (type === 'financialFeasibility') {
      setDialogItem(item || { 
        aspect: '', 
        description: '', 
        cost: '', 
        roi: '', 
        paybackPeriod: '', 
        feasibility: 'Feasible', 
        assumptions: [], 
        risks: [] 
      });
    } else if (type === 'riskAssessment') {
      setDialogItem(item || { 
        risk: '', 
        description: '', 
        category: '', 
        impact: 'Medium', 
        probability: 'Medium', 
        mitigation: '', 
        contingency: '' 
      });
    } else if (type === 'recommendations') {
      setDialogItem(item || { 
        recommendation: '', 
        description: '', 
        priority: 'Medium', 
        rationale: '', 
        implementation: '', 
        timeline: '', 
        dependencies: [] 
      });
    }
    
    setDialogItemIndex(index);
    setOpenDialog(true);
  };
  
  const handleSaveDialog = () => {
    const newData = { ...editingStudy };
    
    if (dialogItemIndex !== null) {
      // Edit existing item
      newData[dialogType][dialogItemIndex] = dialogItem;
    } else {
      // Add new item
      newData[dialogType].push(dialogItem);
    }
    
    setEditingStudy(newData);
    setOpenDialog(false);
  };
  
  const handleDeleteItem = (type, index) => {
    const newData = { ...editingStudy };
    newData[type].splice(index, 1);
    setEditingStudy(newData);
  };
  
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };
  
  const renderTechnicalFeasibilityCard = (item, index) => {
    const getFeasibilityColor = (feasibility) => {
      switch (feasibility) {
        case 'Feasible': return 'success';
        case 'Challenging': return 'warning';
        case 'Not Feasible': return 'error';
        default: return 'default';
      }
    };
    
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Typography variant="h6" gutterBottom>
              {item.aspect}
            </Typography>
            <Chip 
              label={item.feasibility} 
              color={getFeasibilityColor(item.feasibility)}
              size="small"
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {item.description}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            <strong>Assessment:</strong> {item.assessment}
          </Typography>
          
          {item.timeline && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Timeline:</strong> {item.timeline}
            </Typography>
          )}
          
          {item.challenges && item.challenges.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Challenges:</strong>
              </Typography>
              <List dense>
                {item.challenges.map((challenge, idx) => (
                  <ListItem key={idx} sx={{ pl: 2 }}>
                    <ListItemText primary={`• ${challenge}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {item.requirements && item.requirements.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Requirements:</strong>
              </Typography>
              <List dense>
                {item.requirements.map((req, idx) => (
                  <ListItem key={idx} sx={{ pl: 2 }}>
                    <ListItemText primary={`• ${req}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog('technicalFeasibility', item, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem('technicalFeasibility', index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderBusinessFeasibilityCard = (item, index) => {
    const getFeasibilityColor = (feasibility) => {
      switch (feasibility) {
        case 'Feasible': return 'success';
        case 'Challenging': return 'warning';
        case 'Not Feasible': return 'error';
        default: return 'default';
      }
    };
    
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Typography variant="h6" gutterBottom>
              {item.aspect}
            </Typography>
            <Chip 
              label={item.feasibility} 
              color={getFeasibilityColor(item.feasibility)}
              size="small"
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {item.description}
          </Typography>
          
          {item.marketAnalysis && (
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Market Analysis:</strong> {item.marketAnalysis}
            </Typography>
          )}
          
          {item.success && (
            <Typography variant="body2" color="text.secondary" paragraph>
              <strong>Success Criteria:</strong> {item.success}
            </Typography>
          )}
          
          {item.benefits && item.benefits.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Benefits:</strong>
              </Typography>
              <List dense>
                {item.benefits.map((benefit, idx) => (
                  <ListItem key={idx} sx={{ pl: 2 }}>
                    <ListItemText primary={`• ${benefit}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {item.challenges && item.challenges.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Challenges:</strong>
              </Typography>
              <List dense>
                {item.challenges.map((challenge, idx) => (
                  <ListItem key={idx} sx={{ pl: 2 }}>
                    <ListItemText primary={`• ${challenge}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog('businessFeasibility', item, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem('businessFeasibility', index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderFinancialFeasibilityCard = (item, index) => {
    const getFeasibilityColor = (feasibility) => {
      switch (feasibility) {
        case 'Feasible': return 'success';
        case 'Challenging': return 'warning';
        case 'Not Feasible': return 'error';
        default: return 'default';
      }
    };
    
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Typography variant="h6" gutterBottom>
              {item.aspect}
            </Typography>
            <Chip 
              label={item.feasibility} 
              color={getFeasibilityColor(item.feasibility)}
              size="small"
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {item.description}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Cost:</strong> {item.cost}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>ROI:</strong> {item.roi}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Payback Period:</strong> {item.paybackPeriod}
              </Typography>
            </Grid>
          </Grid>
          
          {item.assumptions && item.assumptions.length > 0 && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Assumptions:</strong>
              </Typography>
              <List dense>
                {item.assumptions.map((assumption, idx) => (
                  <ListItem key={idx} sx={{ pl: 2 }}>
                    <ListItemText primary={`• ${assumption}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          
          {item.risks && item.risks.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Financial Risks:</strong>
              </Typography>
              <List dense>
                {item.risks.map((risk, idx) => (
                  <ListItem key={idx} sx={{ pl: 2 }}>
                    <ListItemText primary={`• ${risk}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog('financialFeasibility', item, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem('financialFeasibility', index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderRiskAssessmentCard = (risk, index) => {
    const getImpactColor = (impact) => {
      switch (impact) {
        case 'High': return 'error';
        case 'Medium': return 'warning';
        case 'Low': return 'success';
        default: return 'default';
      }
    };
    
    const getProbabilityColor = (probability) => {
      switch (probability) {
        case 'High': return 'error';
        case 'Medium': return 'warning';
        case 'Low': return 'success';
        default: return 'default';
      }
    };
    
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {risk.risk}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {risk.description}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="body2" color="text.secondary">
                <strong>Category:</strong> {risk.category}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  <strong>Impact:</strong>
                </Typography>
                <Chip 
                  label={risk.impact} 
                  color={getImpactColor(risk.impact)}
                  size="small"
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  <strong>Probability:</strong>
                </Typography>
                <Chip 
                  label={risk.probability} 
                  color={getProbabilityColor(risk.probability)}
                  size="small"
                />
              </Box>
            </Grid>
          </Grid>
          
          {risk.mitigation && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Mitigation:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {risk.mitigation}
              </Typography>
            </Box>
          )}
          
          {risk.contingency && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Contingency:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {risk.contingency}
              </Typography>
            </Box>
          )}
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog('riskAssessment', risk, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem('riskAssessment', index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderRecommendationCard = (rec, index) => {
    const getPriorityColor = (priority) => {
      switch (priority) {
        case 'High': return 'error';
        case 'Medium': return 'warning';
        case 'Low': return 'success';
        default: return 'default';
      }
    };
    
    return (
      <Card key={index} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
            <Typography variant="h6" gutterBottom>
              {rec.recommendation}
            </Typography>
            <Chip 
              label={rec.priority} 
              color={getPriorityColor(rec.priority)}
              size="small"
            />
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            {rec.description}
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Timeline:</strong> {rec.timeline}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Rationale:</strong> {rec.rationale}
              </Typography>
            </Grid>
          </Grid>
          
          {rec.implementation && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Implementation:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {rec.implementation}
              </Typography>
            </Box>
          )}
          
          {rec.dependencies && rec.dependencies.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Dependencies:</strong>
              </Typography>
              <List dense>
                {rec.dependencies.map((dep, idx) => (
                  <ListItem key={idx} sx={{ pl: 2 }}>
                    <ListItemText primary={`• ${dep}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </CardContent>
        {editMode && (
          <CardActions>
            <IconButton 
              size="small" 
              onClick={() => handleOpenDialog('recommendations', rec, index)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => handleDeleteItem('recommendations', index)}
            >
              <DeleteIcon />
            </IconButton>
          </CardActions>
        )}
      </Card>
    );
  };
  
  const renderSection = (type, title, items, renderCard, icon) => {
    return (
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center">
            {icon}
            <Typography variant="h6" sx={{ ml: 1 }}>{title}</Typography>
            <Chip 
              label={items.length} 
              size="small" 
              sx={{ ml: 2 }} 
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box>
            {editMode && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog(type)}
                sx={{ mb: 2 }}
              >
                Add {title.slice(0, -1)}
              </Button>
            )}
            {items.map((item, index) => renderCard(item, index))}
            {items.length === 0 && (
              <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                No {title.toLowerCase()} defined yet.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };
  
  if (loading && !study.executiveSummary) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <SectionTitle variant="h4">
            <StraightenIcon />
            Feasibility Study
          </SectionTitle>
          <Box>
            {!editMode ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleFetchFromSOW}
                  disabled={aiLoading}
                  sx={{ mr: 1, backgroundColor: '#F5821F' }}
                >
                  {aiLoading ? <CircularProgress size={20} /> : 'Fetch From Project Documents'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={loading}
                  sx={{ mr: 1 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Save'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => {
                    setEditMode(false);
                    setEditingStudy(study);
                  }}
                >
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Analyze technical, business, and financial feasibility for {project?.name}.
        </Typography>
      </Paper>
      
      {/* Executive Summary Section */}
      <Accordion expanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Executive Summary</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {editMode ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Executive Summary"
              value={editingStudy.executiveSummary}
              onChange={(e) => setEditingStudy({
                ...editingStudy,
                executiveSummary: e.target.value
              })}
            />
          ) : (
            <Typography>
              {study.executiveSummary || 'No executive summary available. Click "Edit" to add content or "Fetch From Project Documents" to generate from your project documents.'}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
      
      {/* Technical Feasibility */}
      {renderSection('technicalFeasibility', 'Technical Feasibility', editingStudy.technicalFeasibility, renderTechnicalFeasibilityCard, <EngineeringIcon />)}
      
      {/* Business Feasibility */}
      {renderSection('businessFeasibility', 'Business Feasibility', editingStudy.businessFeasibility, renderBusinessFeasibilityCard, <BusinessIcon />)}
      
      {/* Financial Feasibility */}
      {renderSection('financialFeasibility', 'Financial Feasibility', editingStudy.financialFeasibility, renderFinancialFeasibilityCard, <AttachMoneyIcon />)}
      
      {/* Risk Assessment */}
      {renderSection('riskAssessment', 'Risk Assessment', editingStudy.riskAssessment, renderRiskAssessmentCard, <WarningIcon />)}
      
      {/* Recommendations */}
      {renderSection('recommendations', 'Recommendations', editingStudy.recommendations, renderRecommendationCard, <RecommendIcon />)}
      
      {/* Dialog for adding/editing items */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogItemIndex !== null ? 'Edit' : 'Add'} {
            dialogType === 'technicalFeasibility' ? 'Technical Feasibility' :
            dialogType === 'businessFeasibility' ? 'Business Feasibility' :
            dialogType === 'financialFeasibility' ? 'Financial Feasibility' :
            dialogType === 'riskAssessment' ? 'Risk Assessment' :
            'Recommendation'
          }
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {dialogType === 'technicalFeasibility' && (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Technical Aspect"
                  value={dialogItem.aspect || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, aspect: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  value={dialogItem.description || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, description: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Assessment"
                  value={dialogItem.assessment || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, assessment: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  select
                  label="Feasibility"
                  value={dialogItem.feasibility || 'Feasible'}
                  onChange={(e) => setDialogItem({ ...dialogItem, feasibility: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                >
                  <option value="Feasible">Feasible</option>
                  <option value="Challenging">Challenging</option>
                  <option value="Not Feasible">Not Feasible</option>
                </TextField>
                <TextField
                  fullWidth
                  label="Timeline"
                  value={dialogItem.timeline || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, timeline: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
            
            {dialogType === 'businessFeasibility' && (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Business Aspect"
                  value={dialogItem.aspect || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, aspect: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  value={dialogItem.description || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, description: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Market Analysis"
                  value={dialogItem.marketAnalysis || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, marketAnalysis: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  select
                  label="Feasibility"
                  value={dialogItem.feasibility || 'Feasible'}
                  onChange={(e) => setDialogItem({ ...dialogItem, feasibility: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                >
                  <option value="Feasible">Feasible</option>
                  <option value="Challenging">Challenging</option>
                  <option value="Not Feasible">Not Feasible</option>
                </TextField>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Success Criteria"
                  value={dialogItem.success || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, success: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
            
            {dialogType === 'financialFeasibility' && (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Financial Aspect"
                  value={dialogItem.aspect || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, aspect: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  value={dialogItem.description || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, description: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Cost"
                  value={dialogItem.cost || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, cost: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="ROI"
                  value={dialogItem.roi || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, roi: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Payback Period"
                  value={dialogItem.paybackPeriod || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, paybackPeriod: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  select
                  label="Feasibility"
                  value={dialogItem.feasibility || 'Feasible'}
                  onChange={(e) => setDialogItem({ ...dialogItem, feasibility: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                >
                  <option value="Feasible">Feasible</option>
                  <option value="Challenging">Challenging</option>
                  <option value="Not Feasible">Not Feasible</option>
                </TextField>
              </>
            )}
            
            {dialogType === 'riskAssessment' && (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Risk"
                  value={dialogItem.risk || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, risk: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  value={dialogItem.description || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, description: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Category"
                  value={dialogItem.category || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, category: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  select
                  label="Impact"
                  value={dialogItem.impact || 'Medium'}
                  onChange={(e) => setDialogItem({ ...dialogItem, impact: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </TextField>
                <TextField
                  fullWidth
                  select
                  label="Probability"
                  value={dialogItem.probability || 'Medium'}
                  onChange={(e) => setDialogItem({ ...dialogItem, probability: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </TextField>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Mitigation Strategy"
                  value={dialogItem.mitigation || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, mitigation: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Contingency Plan"
                  value={dialogItem.contingency || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, contingency: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
            
            {dialogType === 'recommendations' && (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Recommendation"
                  value={dialogItem.recommendation || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, recommendation: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description"
                  value={dialogItem.description || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, description: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  select
                  label="Priority"
                  value={dialogItem.priority || 'Medium'}
                  onChange={(e) => setDialogItem({ ...dialogItem, priority: e.target.value })}
                  SelectProps={{ native: true }}
                  sx={{ mb: 2 }}
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </TextField>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Rationale"
                  value={dialogItem.rationale || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, rationale: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Implementation"
                  value={dialogItem.implementation || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, implementation: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Timeline"
                  value={dialogItem.timeline || ''}
                  onChange={(e) => setDialogItem({ ...dialogItem, timeline: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveDialog} variant="contained">
            {dialogItemIndex !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeasibilityStudyPage;